import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface LoadingOperation {
  message: string;
  startTime: number;
}

interface LoadingContextType {
  operations: Map<string, LoadingOperation>;
  isLoading: boolean;
  startLoading: (operationId: string, message?: string) => void;
  stopLoading: (operationId: string) => void;
  getOperationMessage: (operationId: string) => string | undefined;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [operations, setOperations] = useState<Map<string, LoadingOperation>>(new Map());

  const startLoading = useCallback((operationId: string, message: string = 'Loading...') => {
    setOperations(prev => {
      const newOps = new Map(prev);
      newOps.set(operationId, { message, startTime: Date.now() });
      return newOps;
    });

    // Failsafe: Auto-clear after 30 seconds
    setTimeout(() => {
      setOperations(prev => {
        const newOps = new Map(prev);
        if (newOps.has(operationId)) {
          console.warn(`Loading operation "${operationId}" auto-cleared after timeout`);
          newOps.delete(operationId);
        }
        return newOps;
      });
    }, 30000);
  }, []);

  const stopLoading = useCallback((operationId: string) => {
    setOperations(prev => {
      const newOps = new Map(prev);
      newOps.delete(operationId);
      return newOps;
    });
  }, []);

  const getOperationMessage = useCallback((operationId: string) => {
    return operations.get(operationId)?.message;
  }, [operations]);

  const isLoading = operations.size > 0;

  return (
    <LoadingContext.Provider 
      value={{ 
        operations, 
        isLoading, 
        startLoading, 
        stopLoading, 
        getOperationMessage 
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
